import type {Nullable} from '../utility-types';
import type {CheckDevicePermissions, DevicePermissionsResult} from './types';

import {logger} from '../logger';
import {platform} from '../platform';
import {DevicePermission} from './DevicePermission';

export const checkDevicePermissions: CheckDevicePermissions =
  async permissions => {
    if (permissions.length === 0) {
      return {
        hasAllPermissions: false,
      };
    }

    const result: DevicePermissionsResult = {
      hasAllPermissions: true,
    };

    const mediaDevicesPermissions = await checkMediaDevicesPermissions();

    permissions.forEach(permission => {
      result[permission] = mediaDevicesPermissions[permission];
    });

    result.hasAllPermissions = Object.values(result).every(value => value);

    return result;
  };

async function checkMediaDevicesPermissions(): Promise<{
  [DevicePermission.CAMERA]: boolean;
  [DevicePermission.MICROPHONE]: boolean;
}> {
  let permissionsApiError: Nullable<string> = null;

  if (navigator.permissions && !platform.isMobileBrowser) {
    try {
      return await checkPermissionsUsingPermissionsAPI();
    } catch (error) {
      // Do nothing to try check it with mediaDevices
      permissionsApiError = error.message;
    }
  }

  if (navigator.mediaDevices && !platform.isSafari) {
    try {
      return await checkPermissionsUsingMediaDevices();
    } catch (error) {
      logger.error(
        new Error(
          `mediaDevicesError: ${error.message}; ${
            permissionsApiError
              ? `permissionsApiError: ${permissionsApiError}`
              : ''
          }`,
        ),
      );

      return {
        [DevicePermission.CAMERA]: false,
        [DevicePermission.MICROPHONE]: false,
      };
    }
  } else {
    if (permissionsApiError) {
      logger.error(new Error(`permissionsApiError: ${permissionsApiError}`));
    }

    // Safari doesn't support mediaDevices, at least let's try to check it with getUserMedia
    return await checkPermissionsUsingGetUserMedia();
  }
}

async function checkPermissionsUsingGetUserMedia(): Promise<{
  [DevicePermission.CAMERA]: boolean;
  [DevicePermission.MICROPHONE]: boolean;
}> {
  try {
    await navigator.mediaDevices?.getUserMedia({
      audio: true,
      video: true,
    });

    return {
      [DevicePermission.CAMERA]: true,
      [DevicePermission.MICROPHONE]: true,
    };
  } catch (_error) {
    return {
      [DevicePermission.CAMERA]: false,
      [DevicePermission.MICROPHONE]: false,
    };
  }
}

/**
 * This works only with https. It doesn't work on Safari (because Safari always sets device's label, even if it's not granted)
 */
async function checkPermissionsUsingMediaDevices(): Promise<{
  [DevicePermission.CAMERA]: boolean;
  [DevicePermission.MICROPHONE]: boolean;
}> {
  const enumerateDevices = await navigator.mediaDevices?.enumerateDevices?.();

  let isVideoGranted = false;
  let isAudioGranted = false;

  enumerateDevices.forEach(device => {
    if (device.kind === 'videoinput' && device.label?.length > 0) {
      isVideoGranted = true;
    }

    if (device.kind === 'audioinput' && device.label?.length > 0) {
      isAudioGranted = true;
    }
  });

  return {
    [DevicePermission.CAMERA]: isVideoGranted,
    [DevicePermission.MICROPHONE]: isAudioGranted,
  };
}

async function checkPermissionsUsingPermissionsAPI(): Promise<{
  [DevicePermission.CAMERA]: boolean;
  [DevicePermission.MICROPHONE]: boolean;
}> {
  const isCameraGranted =
    (
      await navigator.permissions?.query({
        name: 'camera',
      })
    )?.state === 'granted';
  const isMicroGranted =
    (
      await navigator.permissions?.query({
        name: 'microphone',
      })
    )?.state === 'granted';

  return {
    [DevicePermission.CAMERA]: isCameraGranted,
    [DevicePermission.MICROPHONE]: isMicroGranted,
  };
}
